import { Button, CircularProgress, Grid, Icon } from '@material-ui/core';
import { capitalizeAll, getLocalStorageItem, getRobotTimeSeriesCollectionId, GenericMessageBox, setLocalStorageItem, Time, TimeSeriesType }
    from '@moderepo/mode-app-components';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { RobotInfo } from '@moderepo/mode-apis';
import { DialogResponse, DialogResponseCode, DialogType, modeUIContext, showDialog, showSlideOutPanel, SlideOutPanelLocation }
    from '@moderepo/mode-ui-state';
import { fetchRobotCloudProjectSettings, fetchRobots, rcDataContext, selectRobotCloudProjectSettings, selectRobots }
    from '@moderepo/mode-data-state-robot-cloud';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { RCRobotLatestMetricDisplayContainer } from './RCRobotLatestMetricDisplayContainer';
import { useRitzTranslation } from '../../utils/customHooks';
import { DownloadTSDataCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';


// The key of the local storage where we will store all the robot's set of hidden metrics
const LS_ALL_ROBOT_HIDDEN_METRICS = 'RobotsHiddenMetrics';


/**
 * Get the local storage key used for storing 1 robot metric settings
 * @param robot
 * @param byRobotClass
 */
const getLSKeyForRobotHiddenMetrics = (projectId: number, robot: RobotInfo, byRobotClass: boolean) => {
    if (byRobotClass) {
        return `${projectId}:${robot.robotClass}`;
    }
    return `${projectId}:${robot.homeId}:${robot.id}`;
};



export interface RCHomeRobotsDashboardContainerProps {
    readonly projectId: number;
    readonly userId?: number | undefined;
    readonly homeId: number;
    readonly deviceId?: number;

    /**
     * This Container has an action that it needs to be displayed as a page CTA. In other to do this, the parent container must
     * implement the handler function setPageMainAction and take the provided 'action' and display it on the page. The parent container DOES NOT
     * need to do anything with this action other than displaying it. This container will take care of the UI of the action and will handle all the
     * events.
     */
    readonly setPageMainAction: (action: JSX.Element)=> void;
}


/**
 * This container display the latest metric values for ALL robots belonged in a home
 * @param props
 */
export const RCHomeRobotsDashboardContainer: React.FC<RCHomeRobotsDashboardContainerProps> = (props: RCHomeRobotsDashboardContainerProps) => {

    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const [globalHiddenMetricsSet, setGlobalHiddenMetricsSet] = useState(getLocalStorageItem(LS_ALL_ROBOT_HIDDEN_METRICS, {}));
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const rcProjectSettings = selectRobotCloudProjectSettings(rcDataState, props.projectId);


    // Set the collection's absolute date range by using the project's creationTime as as startDate and NOW as endDate
    // This is just an estimate date bounds because we don't know the actual date bound without fetching the collection info which can be a lot
    // of data to load because a gateway can have many collections
    const collectionDateBounds = useMemo((): {
        readonly minDate: number;
        readonly maxDate: number;
    } | undefined => {
        if (project) {
            return {
                minDate: Date.parse(project.creationTime),
                maxDate: Date.now(),
            };
        }
        return undefined;
    }, [project]);


    const allRobots = selectRobots(rcDataState, props.projectId, {
        pageNumber: 0,
        pageSize  : 100,
        homeId    : props.homeId,
        deviceId  : props.deviceId,
    });


    // This useEffect is used for loading Robots on component init
    useEffect(() => {
        if (!allRobots) {
            try {
                rcDataDispatch(fetchRobots(props.projectId, {
                    pageNumber: 0,
                    pageSize  : 100,
                    homeId    : props.homeId,
                    deviceId  : props.deviceId,
                }));
            } catch (error) {
                // ignore error
            }
        }
    }, [allRobots, props.deviceId, props.homeId, props.projectId, rcDataDispatch]);


    /**
     * This useEffect is used for fetching RC project settings on component init
     */
    useEffect(() => {
        if (!rcProjectSettings) {
            rcDataDispatch(fetchRobotCloudProjectSettings(props.projectId)).catch(() => {});
        }
    }, [props.projectId, rcDataDispatch, rcProjectSettings]);


    /**
     * On metric settings changed from one of the RCRobotLatestMetricDisplayContainer. When one of the robot metric settings changed,
     * we will save the settings to local storage so that we can reload it later restore the settings. Also, we will check if the user
     * want to apply the settings to all robot of the same class. If yes, we will apply the same settings to all robots of the same class.
     */
    const onHiddenMetricsListChanged = useCallback((robot: RobotInfo, hiddenMetrics: {
        readonly numericMetrics: {
            readonly [metricName: string]: boolean;
        };
        readonly tagMetrics: {
            readonly [metricName: string]: boolean;
        };
    }) => {

        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.confirm_hide_robot_metrics.title',
            message       : 'dialog.confirm_hide_robot_metrics.message',
            messageData   : { robot_class: robot.robotClass },
            positiveButton: 'label.yes',
            negativeButton: 'label.no',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response) {
                    // get the LS key which is used for storing Metric settings
                    const metricLSKey = getLSKeyForRobotHiddenMetrics(props.projectId, robot, response.code === DialogResponseCode.POSITIVE);

                    const updatedGlobalMetricsSettings = {
                        ...globalHiddenMetricsSet,
                        [metricLSKey]: hiddenMetrics,
                    };

                    if (response.code === DialogResponseCode.POSITIVE) {
                        // delete the metric settings by robotId for this robot if there is one exist because we don't need specific settings
                        // for this robot anymore
                        const deleteMetricKey = getLSKeyForRobotHiddenMetrics(props.projectId, robot, false);
                        delete updatedGlobalMetricsSettings[deleteMetricKey];
                    }

                    // Update LOCAL state
                    setGlobalHiddenMetricsSet(updatedGlobalMetricsSettings);

                    // update localStorage
                    setLocalStorageItem(LS_ALL_ROBOT_HIDDEN_METRICS, updatedGlobalMetricsSettings);
                }
            },

        }));

    }, [globalHiddenMetricsSet, props.projectId, uiDispatch]);


    /**
     * On Download Data clicked handler. Show the Download Data Request panel and pass it the collections the user is trying to download
     * and set the default selection data range to 1 Month
     */
    const onDownloadDataClicked = useCallback((selectedRobot?: RobotInfo) => {
        if (rcProjectSettings && collectionDateBounds && allRobots) {
            const options: DownloadTSDataCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : SlideOutPanelCompType.DOWNLOAD_TS_DATA_REQUEST,
                compProps: {
                    data: {
                        projectId        : props.projectId,
                        homeId           : props.homeId,
                        tsdbSmartModuleId: rcProjectSettings.tsdbSmartModuleId,
                        dataInfo         : allRobots.items.map((robot: RobotInfo) => {
                            const collectionId = getRobotTimeSeriesCollectionId(robot.robotClass, robot.id);
                            return {
                                type    : TimeSeriesType.COLLECTION,
                                id      : collectionId,
                                name    : robot.name || robot.id,
                                selected: !selectedRobot || selectedRobot.id === robot.id,
                            };
                        }),
                        startDate: Math.max(collectionDateBounds.minDate, collectionDateBounds.maxDate - Time.MONTH_IN_MS),
                        endDate  : collectionDateBounds.maxDate,
                        minDate  : collectionDateBounds.minDate,
                        maxDate  : collectionDateBounds.maxDate,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [allRobots, collectionDateBounds, props.homeId, props.projectId, rcProjectSettings, uiDispatch]);


    /**
     * Create a Download Button which will be passed to the parent container for display
     */
    const downloadButton = useMemo(() => {
        return (
            <Button
                variant="contained"
                color="primary"
                startIcon={<Icon>get_app</Icon>}
                disabled={!(rcProjectSettings && collectionDateBounds && allRobots)}
                onClick={() => {
                    onDownloadDataClicked();
                }}
            >{capitalizeAll(trans('label.download_time_series_data'))}
            </Button>
        );
    }, [allRobots, collectionDateBounds, onDownloadDataClicked, rcProjectSettings, trans]);


    /**
     * on component init, call the parent's setExternalComp handler function and pass it the download button to display
     */
    useEffect(() => {
        props.setPageMainAction(downloadButton);
    }, [downloadButton, props, props.setPageMainAction]);


    return (
        <>
            {allRobots && (
                <Grid container spacing={3}>
                    {allRobots.items.map((robot: RobotInfo) => {

                        // Get the metric settings for the robot. First try to get the specific metric settings for the robot by ID. If that
                        // settings do not exist, try getting the metric settings by robotClass.
                        const robotHiddenMetrics = globalHiddenMetricsSet[getLSKeyForRobotHiddenMetrics(props.projectId, robot, false)]
                            || globalHiddenMetricsSet[getLSKeyForRobotHiddenMetrics(props.projectId, robot, true)];

                        return (
                            <Grid item key={`${robot.homeId}-${robot.id}`} xs={12} sm={6} md={6} lg={4} xl={3}>
                                <RCRobotLatestMetricDisplayContainer
                                    projectId={props.projectId}
                                    userId={props.userId}
                                    homeId={robot.homeId}
                                    robotId={robot.id}
                                    hiddenMetrics={robotHiddenMetrics}
                                    onHiddenMetricsListChanged={(hiddenMetrics: {
                                        readonly numericMetrics: {
                                            readonly [fieldName: string]: boolean;
                                        };
                                        readonly tagMetrics: {
                                            readonly [fieldName: string]: boolean;
                                        };
                                    }) => {
                                        onHiddenMetricsListChanged(robot, hiddenMetrics);
                                    }}
                                    onDownloadDataClicked={onDownloadDataClicked}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {allRobots && allRobots.items.length === 0 && (
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.robot', { count: 2 })),
                    })}
                />
            )}

            {!allRobots && (
                <CircularProgress />
            )}
        </>
    );
};
